import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Circle, Delete, Grid3x3, Mic, MicOff, Pause, Phone, PhoneForwarded, PhoneOff, Play, TriangleAlert, UserPlus, Users, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { del, get, post } from "../../api/client";
import type { ActiveCall, CallerId } from "../../api/types";
import { useCan } from "../../auth/session";
import { Button } from "../../components/ui/Button";
import { SelectField } from "../../components/ui/Field";
import { Badge, InlineError } from "../../components/ui/misc";
import { toast } from "../../components/ui/toast";
import { STATUS_LABELS, formatPhone } from "../../lib/format";
import { useDialer } from "../../voice/dialer";
import { voice } from "../../voice/manager";
import { useVoice } from "../../voice/store";
import { activeCallsKey, useActiveCalls } from "../../voice/useActiveCalls";
import { CallTimer } from "./CallTimer";
import { AddPeopleDialog } from "./AddPeopleDialog";
import { ColleaguesDialog, StatusDot, matchExtension, statusLabel, useDirectory } from "./Colleagues";
import { ContactSuggestions, SUGGESTION_LIST_ID, suggestionId, useContactSuggestions } from "./ContactSuggestions";
import { Keypad } from "./Keypad";
import { isToneKey, playTone } from "../../lib/dtmf";
import { TransferDialog } from "./TransferDialog";

/** Picks the call this browser is handling: the one whose media leg is open here, else the newest live one. */
function pickCurrent(active: ActiveCall[], activeCallSid: string | null): ActiveCall | null {
  return (
    active.find((c) => c.myLeg?.callSid && c.myLeg.callSid === activeCallSid) ??
    active.find((c) => c.myLeg?.status === "IN_PROGRESS") ??
    active.find((c) => c.status === "INITIATING") ??
    null
  );
}

export function Softphone() {
  const { data: active = [], refetch } = useActiveCalls();
  const v = useVoice();
  const current = pickCurrent(active, v.activeCallSid);
  const inCall = !!v.activeCallSid || v.sdkCallState === "connecting" || v.sdkCallState === "ringing" || (!!current && current.myLeg?.status === "IN_PROGRESS");

  return (
    <div className="flex flex-col gap-4 p-5">
      {v.status === "error" || v.status === "unsupported" ? (
        <div role="alert" className="flex gap-2 rounded-2xl bg-rose-500/10 p-3 text-sm text-rose-800 ring-1 ring-inset ring-rose-500/20">
          <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-medium">Softphone unavailable</p>
            <p>{v.error}</p>
            {v.status === "error" && <button className="focus-ring mt-1 underline" onClick={() => void voice.stop().then(() => voice.start())}>Reconnect</button>}
          </div>
        </div>
      ) : null}
      {inCall ? <ActiveCallPanel call={current} onChanged={() => void refetch()} /> : <Dialer />}
      {!inCall && v.lastOutboundCallId && <LastCallOutcome callId={v.lastOutboundCallId} />}
      {v.lastCallError && !inCall && <InlineError error={{ message: `Last call ended with an error: ${v.lastCallError}` }} />}
    </div>
  );
}

/** Explains, from the server's record, why the last outbound call didn't connect (e.g. a Twilio account restriction). */
function LastCallOutcome({ callId }: { callId: string }) {
  const { canAny } = useCan();
  const q = useQuery({
    queryKey: ["calls", "outcome", callId],
    queryFn: () => get<{ status: string; endReason: string | null; failureReason: string | null; answeredAt: string | null }>(`/calls/${callId}`),
    enabled: canAny("calls.read.own", "calls.read.team", "calls.read.organization"),
    // Final status can lag the browser hang-up by a moment; poll briefly until it settles.
    refetchInterval: (query) => (query.state.data && !["INITIATING", "RINGING", "CONNECTED", "ON_HOLD", "TRANSFERRING"].includes(query.state.data.status) ? false : 1500),
    retry: false,
  });
  const d = q.data;
  if (!d || d.answeredAt) return null;
  const reason =
    d.failureReason ??
    (d.status === "BUSY" ? "The number you called was busy." : d.status === "NO_ANSWER" ? "Nobody answered." : d.status === "FAILED" ? "The call could not be connected." : null);
  if (!reason) return null;
  return (
    <div role="alert" className="flex items-start gap-2 rounded-2xl bg-amber-500/10 p-3 text-sm text-amber-900 ring-1 ring-inset ring-amber-500/25">
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="flex-1">
        <p className="font-medium">Your last call didn't connect</p>
        <p className="mt-0.5">{reason}</p>
      </div>
      <button className="focus-ring rounded-lg px-1.5 text-xs text-amber-800 hover:bg-white/60" onClick={() => useVoice.getState().set({ lastOutboundCallId: null })}>Dismiss</button>
    </div>
  );
}

function Dialer() {
  const { can } = useCan();
  const qc = useQueryClient();
  const status = useVoice((s) => s.status);
  const outgoingAllowed = useVoice((s) => s.outgoingAllowed);
  const [number, setNumber] = useState("");
  const [lineId, setLineId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dismissed, setDismissed] = useState(false);
  const found = useContactSuggestions(number, can("contacts.read"));
  const suggestions = dismissed ? [] : found;
  const active = activeIndex < suggestions.length ? activeIndex : -1;
  const editNumber = (next: string) => {
    setNumber(next);
    setActiveIndex(-1);
    setDismissed(false);
  };
  const pick = (c: { phone: string }) => {
    setNumber(c.phone);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.repeat && isToneKey(e.key)) playTone(e.key);
    if (suggestions.length === 0) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      // Cycles through the suggestions and back to the typed number (-1).
      const next = active + step;
      setActiveIndex(next >= suggestions.length ? -1 : next < -1 ? suggestions.length - 1 : next);
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault(); // choose the contact instead of calling the partial number
      pick(suggestions[active]!);
    } else if (e.key === "Escape") {
      setDismissed(true);
      setActiveIndex(-1);
    }
  };
  const callerIds = useQuery({
    queryKey: ["voice", "caller-ids"],
    queryFn: () => get<{ items: CallerId[] }>("/voice/caller-ids").then((r) => r.items),
    enabled: can("calls.initiate"),
    staleTime: 60_000,
  });
  const selectedLine = lineId || callerIds.data?.[0]?.id || "";
  const directory = useDirectory();
  const [showColleagues, setShowColleagues] = useState(false);
  // Short all-digit input that matches a colleague's extension calls them directly.
  const colleague = matchExtension(directory.data, number);
  const ready = status === "ready" && outgoingAllowed && can("calls.initiate");
  const disabledReason = !can("calls.initiate")
    ? "You don't have permission to place calls."
    : status !== "ready" ? "Waiting for the softphone to connect." : !selectedLine && !colleague ? "No caller ID is available to your organization." : null;

  const startCall = async (to: string, target?: { extension?: string; membershipId?: string }) => {
    if (busy || (!to.trim() && !target)) return;
    setBusy(true);
    setError(null);
    let callId: string | null = null;
    try {
      const internal = target ?? (matchExtension(directory.data, to) ? { extension: matchExtension(directory.data, to)!.extension } : null);
      const res = internal
        ? await post<{ callId: string }>("/calls/internal", { ...internal, clientRequestId: crypto.randomUUID() })
        : await post<{ callId: string }>("/calls/outbound", { to: to.trim(), phoneNumberId: selectedLine, clientRequestId: crypto.randomUUID() });
      callId = res.callId;
      useVoice.getState().set({ lastOutboundCallId: res.callId });
      await voice.connectOutbound(res.callId);
      await qc.invalidateQueries({ queryKey: activeCallsKey });
    } catch (err) {
      setError(err);
      // Release the authorized-but-unconnected call so it does not count against limits.
      if (callId) await post(`/calls/${callId}/end`).catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  // Dial requests from other pages (contacts, history) arrive through the dialer store.
  const latest = useRef({ ready, selectedLine, startCall });
  useEffect(() => {
    latest.current = { ready, selectedLine, startCall };
  });
  useEffect(() => {
    const handle = (req: { number: string } | null) => {
      if (!req) return;
      useDialer.getState().clear();
      editNumber(req.number);
      const { ready: ok, selectedLine: line, startCall: call } = latest.current;
      if (ok && line) void call(req.number);
      else inputRef.current?.focus();
    };
    handle(useDialer.getState().request);
    return useDialer.subscribe((s) => handle(s.request));
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void startCall(number);
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3" aria-label="Dialer">
      {callerIds.data && callerIds.data.length > 1 && (
        <SelectField label="Call from" value={selectedLine} onChange={(e) => setLineId(e.target.value)}>
          {callerIds.data.map((n) => <option key={n.id} value={n.id}>{n.label} · {formatPhone(n.e164)}</option>)}
        </SelectField>
      )}
      {callerIds.data?.length === 1 && <p className="text-xs text-subtle">Calling from {callerIds.data[0]!.label} · {formatPhone(callerIds.data[0]!.e164)}</p>}
      <div className="flex items-center gap-1">
        <label htmlFor="dial-number" className="sr-only">Phone number</label>
        <input
          ref={inputRef}
          id="dial-number"
          type="tel"
          inputMode="tel"
          autoComplete="off"
          value={number}
          onChange={(e) => editNumber(e.target.value.replace(/[^\d+*#()\-\s.]/g, ""))}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={suggestions.length > 0}
          aria-controls={SUGGESTION_LIST_ID}
          aria-activedescendant={active >= 0 ? suggestionId(active) : undefined}
          placeholder="Enter a number"
          className="focus-ring h-14 w-full rounded-2xl border border-white/70 bg-white/60 px-3 text-center text-2xl font-light tracking-[0.08em] text-default shadow-[inset_0_1px_3px_rgb(15_23_42/0.08)] backdrop-blur tabular placeholder:text-base placeholder:tracking-normal placeholder:text-subtle"
        />
        {number && (
          <button type="button" aria-label="Delete last digit" className="focus-ring rounded-xl p-2 text-subtle hover:bg-white/70 hover:text-default" onClick={() => editNumber(number.slice(0, -1))}>
            <Delete className="h-5 w-5" />
          </button>
        )}
      </div>
      {colleague ? (
        <p className="flex items-center justify-center gap-1.5 text-sm text-muted" role="status">
          <StatusDot status={colleague.status} /> Ext. {colleague.extension} · <span className="font-medium text-default">{colleague.name}</span> · {statusLabel(colleague.status)}
        </p>
      ) : (
        <ContactSuggestions items={suggestions} number={number} activeIndex={active} onPick={pick} />
      )}
      <Keypad onPress={(d) => editNumber(number + d)} />
      <Button type="submit" variant="success" size="lg" loading={busy} disabled={!ready || (!selectedLine && !colleague) || !number.trim()}>
        <Phone className="h-5 w-5" aria-hidden="true" /> {colleague ? `Call ${colleague.name.split(" ")[0]}` : "Call"}
      </Button>
      {can("calls.initiate") && (
        <Button variant="ghost" size="sm" onClick={() => setShowColleagues(true)}>
          <Users className="h-4 w-4" aria-hidden="true" /> Colleagues{directory.data?.me.extension ? ` · your ext. ${directory.data.me.extension}` : ""}
        </Button>
      )}
      <ColleaguesDialog
        open={showColleagues} onOpenChange={setShowColleagues}
        onCall={(c) => { setShowColleagues(false); editNumber(c.extension); void startCall(c.extension, { membershipId: c.membershipId }); }}
      />
      {disabledReason && <p className="text-center text-xs text-subtle">{disabledReason}</p>}
      <InlineError error={error} />
    </form>
  );
}

function ActiveCallPanel({ call, onChanged }: { call: ActiveCall | null; onChanged: () => void }) {
  const { can, canAny } = useCan();
  const v = useVoice();
  const [pending, setPending] = useState<string | null>(null);
  const [showKeypad, setShowKeypad] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const hasMedia = !!v.activeCallSid;
  const status = call?.status;
  const isTransferTarget = call?.role === "transfer_target";
  const internal = !!call?.internal;
  const people = call?.participants ?? [];
  const hasAdded = people.some((p) => p.added);
  const conference = people.length > 0;
  // Hold and transfer need a customer leg, which calls between colleagues don't have.
  const controllable = !!call && !isTransferTarget && !internal && (status === "CONNECTED" || status === "ON_HOLD");
  const name = internal ? (call?.peer?.name ?? "Colleague") : call?.contact?.name ?? (call ? formatPhone(call.externalNumber) : "Connecting…");
  const stateLabel = !call
    ? v.sdkCallState === "ringing" ? "Ringing" : "Connecting"
    : call.transfer
      ? call.transfer.status === "CONSULTING" ? "Consulting" : "Transferring"
      : STATUS_LABELS[call.status];

  const run = async (key: string, fn: () => Promise<unknown>, success?: string) => {
    setPending(key);
    setError(null);
    try {
      await fn();
      if (success) toast.success(success);
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setPending(null);
    }
  };

  const hangUp = () => {
    if (hasMedia) voice.hangup();
    else if (call) void run("end", () => post(`/calls/${call.id}/end`));
  };

  const transferTarget = call?.transfer
    ? "name" in call.transfer.target ? call.transfer.target.name : formatPhone(call.transfer.target.number)
    : null;

  return (
    <section aria-label="Active call" className="flex flex-col gap-4">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#4263FF]/15 via-white/40 to-[#38D9FF]/20 p-5 text-center ring-1 ring-inset ring-white/70">
        <div aria-hidden="true" className="pointer-events-none absolute -top-10 left-1/2 h-24 w-40 -translate-x-1/2 rounded-full bg-[#4263FF]/25 blur-2xl" />
        <p className="relative text-[11px] font-semibold uppercase tracking-[0.14em] text-accent-600">{internal ? "Call with a colleague" : conference ? "Conference call" : call?.direction === "INBOUND" ? "Inbound call" : "Outbound call"}</p>
        <p className="relative mt-1 truncate text-2xl font-semibold tracking-tight text-default">{name}</p>
        {!internal && call?.contact && <p className="text-sm text-muted">{formatPhone(call.externalNumber)}</p>}
        <p className="mt-2 flex items-center justify-center gap-2 text-sm text-muted" aria-live="polite">
          <span>{stateLabel}</span>
          {call?.answeredAt && <>·<CallTimer since={call.answeredAt} /></>}
        </p>
        <div className="mt-2 flex flex-wrap justify-center gap-1">
          {call?.status === "ON_HOLD" && <Badge tone="amber">On hold</Badge>}
          {v.muted && <Badge tone="red">Muted</Badge>}
          {call?.recording && <Badge tone="red"><Circle className="mr-1 h-2 w-2 fill-current" aria-hidden="true" />Recording</Badge>}
          {v.sdkCallState === "reconnecting" && <Badge tone="amber">Reconnecting</Badge>}
        </div>
      </div>

      {call?.addedBy && <p className="text-center text-xs text-muted">{call.addedBy.name} added you to this call.</p>}
      {conference && (
        <section aria-label="In this call" className="rounded-2xl bg-white/50 p-3 ring-1 ring-inset ring-white/70">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-subtle">In this call</p>
          <ul className="space-y-1.5">
            {people.map((p) => {
              const who = p.name ?? formatPhone(p.number);
              return (
                <li key={p.id} className="flex items-center gap-2 text-sm">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${p.status === "IN_PROGRESS" ? "bg-emerald-500" : "animate-pulse bg-amber-500"}`} aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate text-default">{who}</span>
                  <span className="text-xs text-subtle">{p.status === "IN_PROGRESS" ? "Joined" : "Ringing…"}</span>
                  {p.added && controllable && can("calls.transfer") && (
                    <button className="focus-ring rounded-lg p-1 text-subtle hover:bg-white/70 hover:text-rose-600" aria-label={`Remove ${who} from the call`}
                      onClick={() => call && run(`remove-${p.id}`, () => del(`/calls/${call.id}/participants/${p.id}`), `${who} removed`)}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {v.callWarning && <p role="status" className="rounded-xl bg-amber-500/10 px-3 py-2 text-xs text-amber-800 ring-1 ring-inset ring-amber-500/25">{v.callWarning}</p>}

      {call?.transfer && !isTransferTarget && (
        <div className="rounded-2xl bg-white/50 p-3 text-sm ring-1 ring-inset ring-white/70" role="status">
          <p className="font-medium text-default">
            {call.transfer.type === "ATTENDED" ? "Consult transfer" : "Transfer"} to {transferTarget}
          </p>
          <p className="text-muted">{call.transfer.status === "CONSULTING" ? "You are talking to the recipient. The caller is on hold." : "Ringing… the caller is on hold."}</p>
          {call.transfer.type === "ATTENDED" && (
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="primary" disabled={call.transfer.status !== "CONSULTING"} loading={pending === "complete"}
                onClick={() => run("complete", () => post(`/calls/${call.id}/transfer/complete`), "Transfer completed")}>
                Complete transfer
              </Button>
              <Button size="sm" loading={pending === "cancel"} onClick={() => run("cancel", () => post(`/calls/${call.id}/transfer/cancel`), "Returned to caller")}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <ControlButton
          label={v.muted ? "Unmute" : "Mute"}
          pressed={v.muted}
          icon={v.muted ? MicOff : Mic}
          disabled={!hasMedia || !can("calls.mute")}
          onClick={() => voice.toggleMute()}
        />
        {call?.status === "ON_HOLD" ? (
          <ControlButton label="Resume" icon={Play} loading={pending === "resume"} disabled={!controllable || !can("calls.resume")}
            onClick={() => run("resume", () => post(`/calls/${call.id}/resume`))} />
        ) : (
          <ControlButton label="Hold" icon={Pause} loading={pending === "hold"} disabled={!controllable || !can("calls.hold") || !!call?.transfer}
            onClick={() => call && run("hold", () => post(`/calls/${call.id}/hold`))} />
        )}
        <ControlButton label="Transfer" icon={PhoneForwarded} disabled={!controllable || !can("calls.transfer") || !!call?.transfer || hasAdded}
          onClick={() => setShowTransfer(true)} />
        <ControlButton label="Add people" icon={UserPlus} disabled={!controllable || !can("calls.transfer") || !!call?.transfer}
          onClick={() => setShowAdd(true)} />
        <ControlButton label="Keypad" icon={Grid3x3} pressed={showKeypad} disabled={!hasMedia} onClick={() => setShowKeypad((s) => !s)} />
      </div>

      {showKeypad && hasMedia && <Keypad label="Send tones" tones={false} onPress={(d) => voice.sendDigits(d)} />}
      <InlineError error={error} />

      <Button variant="danger" size="lg" onClick={hangUp} loading={pending === "end"} disabled={!hasMedia && !canAny("calls.initiate", "calls.receive", "calls.manage.team")}>
        <PhoneOff className="h-5 w-5" aria-hidden="true" /> Hang up
      </Button>
      {call && showAdd && <AddPeopleDialog call={call} open={showAdd} onOpenChange={setShowAdd} onAdded={onChanged} />}
      {call && showTransfer && (
        <TransferDialog callId={call.id} open={showTransfer} onOpenChange={setShowTransfer} onStarted={() => { toast.success("Transfer started"); onChanged(); }} />
      )}
      {!call && hasMedia && <p className="text-center text-xs text-subtle">Waiting for call details…</p>}
      {isTransferTarget && (
        <p className="text-center text-xs text-subtle">A colleague is consulting you about a transfer. Call controls unlock once they complete it.</p>
      )}
    </section>
  );
}

function ControlButton({
  label, icon: Icon, onClick, disabled, pressed, loading,
}: { label: string; icon: typeof Mic; onClick: () => void; disabled?: boolean; pressed?: boolean; loading?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      aria-pressed={pressed}
      aria-busy={loading || undefined}
      className={`focus-ring flex h-16 flex-col items-center justify-center gap-1 rounded-2xl text-xs font-medium ring-1 ring-inset transition-all disabled:cursor-not-allowed disabled:opacity-40 ${pressed ? "bg-accent-gradient text-white shadow-glow ring-transparent" : "bg-white/55 text-default ring-white/70 hover:-translate-y-px hover:bg-white/85"}`}
    >
      <Icon className="h-5 w-5" aria-hidden="true" />
      {loading ? "…" : label}
    </button>
  );
}
